<div class="page-shell">
  <div class="breadcrumb-row">
    <button mat-button type="button" (click)="goBackToList()">
      <mat-icon>arrow_back</mat-icon>
      All Flags
    </button>
    @if (editor.id) {
      <span class="breadcrumb-current">{{ editor.key }}</span>
    }
  </div>

  <div class="page-header">
    <div>
      <h1>{{ editor.id ? editor.key : 'New Managed Feature Flag' }}</h1>
      <p>
        {{ editor.id ? editor.name : 'Create a database-backed flag and define its initial behavior.' }}
      </p>
    </div>
    <div class="environment-card">
      <span class="eyebrow">Connected Backend</span>
      <strong>{{ connectedBackend }}</strong>
      <span class="helper-text">Browser environment: {{ connectedEnvironment }}</span>
    </div>
  </div>

  @if (loading) {
    <div class="loading-state">
      <mat-spinner diameter="28"></mat-spinner>
      <span>Loading managed feature flag…</span>
    </div>
  }

  @if (!loading && loadFailed) {
    <div class="load-error">
      <div>
        <strong>This managed feature flag could not be loaded.</strong>
        <p>It may no longer exist, or the connected backend may be unavailable.</p>
      </div>
      <button mat-stroked-button type="button" (click)="goBackToList()">Return to All Flags</button>
    </div>
  }

  @if (!loading && !loadFailed) {
    <div class="page-grid page-grid-detail">
      <section class="list-panel surface-panel">
        <div class="panel-header">
          <div>
            <h2>Flags</h2>
            <p class="panel-subtitle">{{ filteredFlags.length }} visible in this connected backend.</p>
          </div>
          <button mat-raised-button color="primary" (click)="startCreate()">New Flag</button>
        </div>
        <div class="filters-grid">
          <mat-form-field appearance="outline">
            <mat-label>Search</mat-label>
            <input matInput [(ngModel)]="filters.search" (ngModelChange)="applyFilters()" placeholder="Key, name, description" />
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>App</mat-label>
            <mat-select [(ngModel)]="filters.app" (selectionChange)="applyFilters()">
              <mat-option value="all">All apps</mat-option>
              <mat-option value="team-admin">Team Admin</mat-option>
              <mat-option value="customer-app">Customer App</mat-option>
              <mat-option value="backend">Backend</mat-option>
            </mat-select>
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Status</mat-label>
            <mat-select [(ngModel)]="filters.status" (selectionChange)="applyFilters()">
              <mat-option value="all">All statuses</mat-option>
              <mat-option value="active">Active</mat-option>
              <mat-option value="inactive">Inactive</mat-option>
            </mat-select>
          </mat-form-field>
        </div>
        @if (!loading && filteredFlags.length === 0) {
          <div class="empty-state">
            No managed feature flags match the current filters.
          </div>
        }
        @if (filteredFlags.length > 0) {
          <div class="flag-list">
            @for (flag of filteredFlags; track trackByFlagId($index, flag)) {
              <button
                type="button"
                class="flag-card"
                [class.flag-card-selected]="flag.id === selectedFlagId"
                (click)="selectFlag(flag)">
                <div class="flag-card-topline">
                  <strong>{{ flag.key }}</strong>
                  <div class="flag-card-statuses">
                    <span class="status-pill" [class.status-pill-inactive]="!flag.isActive">
                      {{ flag.isActive ? 'Active' : 'Inactive' }}
                    </span>
                    <span class="status-pill status-pill-default" [class.status-pill-default-off]="!flag.defaultEnabled">
                      {{ flag.defaultEnabled ? 'Default On' : 'Default Off' }}
                    </span>
                  </div>
                </div>
                <div class="flag-card-name">{{ flag.name }}</div>
                <div class="flag-card-apps">{{ flag.appTargets.join(', ') }}</div>
                <div class="flag-card-meta">
                  <span>Schema v{{ flag.schemaVersion }}</span>
                  <span>Rules: {{ flag.rules.length }}</span>
                </div>
              </button>
            }
          </div>
        }
        <ng-template #transferSection>
          <mat-card class="subsection-card transfer-section" [class.subsection-card-collapsed]="!sectionExpanded.importExport">
            <button
              type="button"
              class="subsection-toggle"
              [attr.aria-expanded]="sectionExpanded.importExport"
              (click)="toggleSection('importExport')">
              <div class="subsection-toggle-copy">
                <h3>Import / Export</h3>
                <span class="section-summary">Advanced transfer tools</span>
              </div>
              <mat-icon class="subsection-toggle-icon" [class.subsection-toggle-icon-expanded]="sectionExpanded.importExport">
                expand_more
              </mat-icon>
            </button>
            @if (sectionExpanded.importExport) {
              <div class="subsection-content">
                <p class="helper-text subsection-description">
                  Export the current flag as a transfer blob, then paste it into another Team Admin environment and load it into the editor there.
                </p>
                <div class="transfer-grid">
                  <div class="transfer-panel">
                    <div class="transfer-panel-header">
                      <h4>Export Current Flag</h4>
                      <span class="helper-text">Safe for cross-environment copy/paste</span>
                    </div>
                    <div class="transfer-options">
                      <mat-checkbox
                        [(ngModel)]="exportOptions.clearUserOverrides"
                        (ngModelChange)="refreshExportBlob()">
                        Clear exact user overrides
                      </mat-checkbox>
                      <mat-checkbox
                        [(ngModel)]="exportOptions.clearConditionalRules"
                        (ngModelChange)="refreshExportBlob()">
                        Clear conditional rules
                      </mat-checkbox>
                    </div>
                    <div class="transfer-actions">
                      <button mat-stroked-button type="button" (click)="generateExportBlob()">Generate Export</button>
                      <button mat-raised-button color="primary" type="button" (click)="copyExportBlob()">Copy Export</button>
                    </div>
                    <mat-form-field appearance="outline" class="full-width transfer-textarea">
                      <mat-label>Export blob</mat-label>
                      <textarea
                        matInput
                        rows="14"
                        readonly
                        [value]="exportBlob"
                      placeholder="Generate or copy an export blob from the current editor state."></textarea>
                    </mat-form-field>
                  </div>
                  <div class="transfer-panel">
                    <div class="transfer-panel-header">
                      <h4>Import Into Editor</h4>
                      <span class="helper-text">Loads as a new unsaved flag</span>
                    </div>
                    <p class="helper-text">
                      Paste an exported flag blob here. Loading it into the editor will not save it until you click Save Flag.
                    </p>
                    <mat-form-field appearance="outline" class="full-width transfer-textarea">
                      <mat-label>Import blob</mat-label>
                      <textarea
                        matInput
                        rows="14"
                        [(ngModel)]="importBlob"
                      placeholder='Paste a blob like {"format":"managed-feature-flag-transfer",...}'></textarea>
                    </mat-form-field>
                    <div class="transfer-actions">
                      <button mat-stroked-button type="button" (click)="clearImportBlob()">Clear</button>
                      <button mat-raised-button color="primary" type="button" (click)="loadImportBlob()">Load Into Editor</button>
                    </div>
                  </div>
                </div>
              </div>
            }
          </mat-card>
        </ng-template>
      </section>
      <section class="editor-panel surface-panel">
        <div class="panel-header editor-header">
          <div>
            <h2>Flag Details</h2>
            @if (loadedFlag) {
              <p class="panel-subtitle">
                Last modified {{ loadedFlag.modifiedAt | date:'medium' }}.
              </p>
            } @else {
              <p class="panel-subtitle">Changes are not applied until you save this flag.</p>
            }
          </div>
          <div class="editor-actions">
            @if (hasUnsavedChanges) {
              <span class="unsaved-indicator">Unsaved changes</span>
            }
            <button mat-button type="button" [disabled]="saving || deleting" (click)="goBackToList()">
              Cancel
            </button>
            <button mat-raised-button color="primary" [disabled]="saveDisabled" (click)="saveFlag()">
              {{ saving ? 'Saving...' : 'Save Flag' }}
            </button>
            @if (editor.id) {
              <button
                mat-stroked-button
                color="warn"
                [disabled]="deleting || saving"
                (click)="deleteFlag()">
                Delete
              </button>
            }
          </div>
        </div>
        @if ((editor.id || hasUnsavedChanges) && editorValidationMessage; as validationMessage) {
          <p
            class="validation-message"
            >
            {{ validationMessage }}
          </p>
        }
        @if (loadedFlag?.lifecycle; as lifecycle) {
          <mat-card class="subsection-card lifecycle-summary">
            <h3>Lifecycle and cleanup</h3>
            <p>{{ purposeLabels[lifecycle.purpose] }} · {{ stageLabels[lifecycle.cleanupStage] }}</p>
            <p>Decision: {{ outcomeLabels[lifecycle.intendedOutcome] }}</p>
            <p>Owner: {{ lifecycle.owner || 'Unassigned' }} · Review date (UTC): {{ lifecycle.reviewDueDate || 'Not scheduled' }}</p>
            @for (reason of loadedFlag.lifecycleAssessment?.reasons; track reason) { <span class="lifecycle-reason">{{ reasonLabels[reason] || reason }}</span> }
            <p>{{ loadedFlag.lifecycleAssessment?.nextAction }}</p>
            @if (lifecycle.workItemUrl) { <p><a [href]="lifecycle.workItemUrl" target="_blank" rel="noopener noreferrer">Open cleanup ticket or PR</a></p> }
            <button mat-stroked-button type="button" [disabled]="hasUnsavedChanges || saving || deleting" (click)="reviewLifecycle()">Review lifecycle</button>
            @if (hasUnsavedChanges) { <p class="helper-text">Save flag changes before reviewing its lifecycle.</p> }
          </mat-card>
        } @else { <p class="helper-text">{{ loadedFlag ? 'Lifecycle information is unavailable from this backend.' : 'Save this flag to classify it and schedule a lifecycle review.' }}</p> }
        <div class="editor-grid">
          <mat-form-field appearance="outline">
            <mat-label>Key</mat-label>
            <input matInput [(ngModel)]="editor.key" [readonly]="!!editor.id" placeholder="ORDER_HISTORY" />
            @if (editor.id) {
              <mat-hint>Keys are permanent after a flag is created.</mat-hint>
            }
          </mat-form-field>
          <mat-form-field appearance="outline">
            <mat-label>Name</mat-label>
            <input matInput [(ngModel)]="editor.name" placeholder="Order History" />
          </mat-form-field>
        </div>
        <mat-form-field appearance="outline" class="full-width">
          <mat-label>Description</mat-label>
          <textarea matInput rows="2" [(ngModel)]="editor.description"></textarea>
        </mat-form-field>
        <div class="editor-grid">
          <mat-form-field appearance="outline">
            <mat-label>App Targets</mat-label>
            <mat-select multiple [(ngModel)]="editor.appTargets">
              <mat-option value="team-admin">Team Admin</mat-option>
              <mat-option value="customer-app">Customer App</mat-option>
              <mat-option value="backend">Backend</mat-option>
            </mat-select>
          </mat-form-field>
          <div class="toggle-grid">
            <mat-checkbox [(ngModel)]="editor.isActive">Flag is active</mat-checkbox>
            <mat-checkbox [(ngModel)]="editor.defaultEnabled">Default enabled</mat-checkbox>
          </div>
        </div>
        <mat-card class="subsection-card" [class.subsection-card-collapsed]="!sectionExpanded.userOverrides">
          <button
            type="button"
            class="subsection-toggle"
            [attr.aria-expanded]="sectionExpanded.userOverrides"
            (click)="toggleSection('userOverrides')">
            <div class="subsection-toggle-copy">
              <h3>Exact User Overrides</h3>
              <span class="section-summary">
                {{ editor.includeUserIds.length + editor.excludeUserIds.length }} configured
              </span>
            </div>
            <mat-icon class="subsection-toggle-icon" [class.subsection-toggle-icon-expanded]="sectionExpanded.userOverrides">
              expand_more
            </mat-icon>
          </button>
          @if (sectionExpanded.userOverrides) {
            <div class="subsection-content">
              <div class="subsection-header user-override-header">
                <div>
                  <p class="helper-text subsection-description">
                    Select a user, then choose Include or Exclude. You can also drag IDs between the lists below.
                  </p>
                </div>
                <div class="user-override-actions">
                  <button
                    mat-raised-button
                    color="primary"
                    type="button"
                    [disabled]="!selectedUserOverrideId"
                    (click)="addPendingUserOverride('include')">
                    Include
                  </button>
                  <button
                    mat-raised-button
                    color="warn"
                    type="button"
                    [disabled]="!selectedUserOverrideId"
                    (click)="addPendingUserOverride('exclude')">
                    Exclude
                  </button>
                </div>
              </div>
              <div class="user-search-grid">
                <mat-form-field appearance="outline">
                  <mat-label>Search users</mat-label>
                  <input matInput [(ngModel)]="userSearchQuery" (ngModelChange)="searchUsers()" placeholder="Email or name" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Manual user ID</mat-label>
                  <input matInput [(ngModel)]="manualUserOverrideId" placeholder="UUID" />
                </mat-form-field>
                <button mat-stroked-button class="inline-action" type="button" (click)="selectManualUserOverride()">Select User ID</button>
              </div>
              @if (userSearchResults.length > 0) {
                <div class="user-search-results">
                  @for (user of userSearchResults; track user) {
                    <button
                      type="button"
                      class="user-result"
                      [class.user-result-selected]="user.id === selectedUserOverrideId"
                      (click)="selectSearchedUser(user)">
                      <span>{{ describeUser(user) }}</span>
                      <strong>{{ user.id }}</strong>
                    </button>
                  }
                </div>
              }
              <div class="selected-user-card" [class.selected-user-card-empty]="!selectedUserOverrideId">
                @if (selectedUserOverrideId) {
                  <div>
                    <span class="eyebrow">Selected User</span>
                    <div class="selected-user-meta">
                      <strong>{{ selectedUserOverrideId }}</strong>
                      <span class="helper-text">{{ selectedUserOverrideLabel }}</span>
                    </div>
                  </div>
                } @else {
                  <span class="helper-text">Pick a search result or use a manual user ID to enable the Include and Exclude actions.</span>
                }
                @if (selectedUserOverrideId) {
                  <button
                    mat-button
                    type="button"
                    (click)="clearPendingUserOverrideSelection()">
                    Clear Selection
                  </button>
                }
              </div>
              <div class="override-grid">
                <div class="override-column">
                  <div class="override-column-header">
                    <h4>Include User IDs</h4>
                    <span class="helper-text">{{ editor.includeUserIds.length }}</span>
                  </div>
                  <div
                    id="includeUserIdsDropList"
                    class="chip-list override-drop-list"
                    cdkDropList
                    [cdkDropListData]="editor.includeUserIds"
                    [cdkDropListConnectedTo]="['excludeUserIdsDropList']"
                    (cdkDropListDropped)="dropUserOverride($event)">
                    @if (editor.includeUserIds.length === 0) {
                      <div class="empty-chip-state">
                        Drop a user ID here to include it.
                      </div>
                    }
                    @for (userId of editor.includeUserIds; track trackByUserId($index, userId)) {
                      <div class="chip" cdkDrag>
                        <button class="chip-drag-handle" type="button" cdkDragHandle aria-label="Drag user ID">
                          <mat-icon>drag_indicator</mat-icon>
                        </button>
                        <span class="chip-label">{{ describeUserOverrideById(userId) }}</span>
                        <button mat-icon-button type="button" (click)="removeIncludedUser(userId)">
                          <mat-icon>close</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                </div>
                <div class="override-column">
                  <div class="override-column-header">
                    <h4>Exclude User IDs</h4>
                    <span class="helper-text">{{ editor.excludeUserIds.length }}</span>
                  </div>
                  <div
                    id="excludeUserIdsDropList"
                    class="chip-list override-drop-list"
                    cdkDropList
                    [cdkDropListData]="editor.excludeUserIds"
                    [cdkDropListConnectedTo]="['includeUserIdsDropList']"
                    (cdkDropListDropped)="dropUserOverride($event)">
                    @if (editor.excludeUserIds.length === 0) {
                      <div class="empty-chip-state">
                        Drop a user ID here to exclude it.
                      </div>
                    }
                    @for (userId of editor.excludeUserIds; track trackByUserId($index, userId)) {
                      <div class="chip chip-warn" cdkDrag>
                        <button class="chip-drag-handle" type="button" cdkDragHandle aria-label="Drag user ID">
                          <mat-icon>drag_indicator</mat-icon>
                        </button>
                        <span class="chip-label">{{ describeUserOverrideById(userId) }}</span>
                        <button mat-icon-button type="button" (click)="removeExcludedUser(userId)">
                          <mat-icon>close</mat-icon>
                        </button>
                      </div>
                    }
                  </div>
                </div>
              </div>
            </div>
          }
        </mat-card>
        <mat-card class="subsection-card" [class.subsection-card-collapsed]="!sectionExpanded.conditionalRules">
          <button
            type="button"
            class="subsection-toggle"
            [attr.aria-expanded]="sectionExpanded.conditionalRules"
            (click)="toggleSection('conditionalRules')">
            <div class="subsection-toggle-copy">
              <h3>Conditional Rules</h3>
              <span class="section-summary">
                {{ editor.rules.length }} {{ editor.rules.length === 1 ? 'rule' : 'rules' }}
              </span>
            </div>
            <mat-icon class="subsection-toggle-icon" [class.subsection-toggle-icon-expanded]="sectionExpanded.conditionalRules">
              expand_more
            </mat-icon>
          </button>
          @if (sectionExpanded.conditionalRules) {
            <div class="subsection-content">
              <div class="subsection-header">
                <div>
                  <p class="helper-text subsection-description">
                    Within one rule, the team, company, partner, and state conditions must all match (AND). Include matches any listed value within that condition.
                    To enable teams, companies, or partners independently, use separate Enable rules.
                    Exclude prevents this rule from matching those values; it does not disable the flag by itself. Exact User Overrides take priority over conditional rules.
                  </p>
                </div>
                <button mat-stroked-button type="button" (click)="addRule()">Add Rule</button>
              </div>
              @if (editor.rules.length === 0) {
                <div class="empty-state">
                  No conditional rules yet. Exact user overrides and default behavior still apply.
                </div>
              }
              @for (rule of editor.rules; track trackByRuleId(ruleIndex, rule); let ruleIndex = $index) {
                <mat-card
                  class="rule-card"
                  [class.rule-card-invalid]="getRuleValidationMessage(rule, ruleIndex)"
                  >
                  <div class="rule-card-header">
                    <mat-form-field appearance="outline">
                      <mat-label>Effect</mat-label>
                      <mat-select [(ngModel)]="rule.effect">
                        <mat-option value="enable">Enable</mat-option>
                        <mat-option value="disable">Disable</mat-option>
                      </mat-select>
                    </mat-form-field>
                    <button mat-button color="warn" type="button" (click)="removeRule(rule.id)">Remove Rule</button>
                  </div>
                  <div class="rule-grid">
                    <div class="rule-target-section">
                      <div class="subsection-header user-override-header">
                        <div>
                          <p class="helper-text subsection-description">
                            Search teams by name or ID, then choose Include or Exclude for this rule.
                          </p>
                        </div>
                        <div class="user-override-actions">
                          <button mat-raised-button color="primary" type="button"
                            [disabled]="!rule.selectedTeamId"
                            (click)="addPendingRuleTeam(rule, 'include')">
                            Include
                          </button>
                          <button mat-raised-button color="warn" type="button"
                            [disabled]="!rule.selectedTeamId"
                            (click)="addPendingRuleTeam(rule, 'exclude')">
                            Exclude
                          </button>
                        </div>
                      </div>
                      <div class="user-search-grid target-search-grid">
                        <mat-form-field appearance="outline">
                          <mat-label>Search teams</mat-label>
                          <input matInput [(ngModel)]="rule.teamSearchQuery"
                            (ngModelChange)="searchRuleTeams(rule)"
                            placeholder="Team name or ID" />
                        </mat-form-field>
                        <mat-form-field appearance="outline">
                          <mat-label>Manual team ID</mat-label>
                          <input matInput [(ngModel)]="rule.manualTeamId" placeholder="teamId" />
                        </mat-form-field>
                        <button mat-stroked-button class="inline-action" type="button" (click)="selectManualRuleTeam(rule)">
                          Select Team ID
                        </button>
                      </div>
                      @if (teamSearchFailed) {
                        <p class="helper-text" role="status">
                          Team search is unavailable. Saved team IDs are preserved; you can still enter a manual team ID.
                        </p>
                      }
                      @if (!teamSearchFailed && rule.teamSearchQuery.trim() && rule.teamSearchResults.length === 0) {
                        <p class="helper-text" role="status"
                          >
                          No matching teams.
                        </p>
                      }
                      @if (rule.teamSearchResults.length > 0) {
                        <div class="user-search-results">
                          @for (team of rule.teamSearchResults; track trackByTeamResultId($index, team)) {
                            <button type="button" class="user-result"
                              [class.user-result-selected]="team.id === rule.selectedTeamId"
                              (click)="selectSearchedTeam(rule, team)">
                              <span>{{ team.name }}</span>
                              <strong>{{ team.id }}</strong>
                            </button>
                          }
                        </div>
                      }
                      <div class="selected-user-card" [class.selected-user-card-empty]="!rule.selectedTeamId">
                        @if (rule.selectedTeamId) {
                          <div>
                            <span class="eyebrow">Selected Team</span>
                            <div class="selected-user-meta">
                              <strong>{{ rule.selectedTeamId }}</strong>
                              <span class="helper-text">{{ rule.selectedTeamLabel }}</span>
                            </div>
                          </div>
                        } @else {
                          <span class="helper-text">Pick a search result or use a manual team ID to enable the Include and Exclude actions.</span>
                        }
                        @if (rule.selectedTeamId) {
                          <button mat-button type="button"
                            (click)="clearPendingRuleTeamSelection(rule)">
                            Clear Selection
                          </button>
                        }
                      </div>
                      <div class="override-grid target-override-grid">
                        <div class="override-column">
                          <div class="override-column-header">
                            <h4>Include Teams</h4>
                            <span class="helper-text">{{ rule.teamIds.length }}</span>
                          </div>
                          <div class="chip-list rule-chip-list">
                            @if (rule.teamIds.length === 0) {
                              <div class="empty-chip-state">Add a team here to include it.</div>
                            }
                            @for (teamId of rule.teamIds; track trackByTeamId($index, teamId)) {
                              <div class="chip">
                                <span class="chip-label">{{ describeTeamById(teamId) }}</span>
                                <button mat-icon-button type="button" [attr.aria-label]="'Remove included team ' + describeTeamById(teamId)" (click)="removeIncludedTeam(rule, teamId)">
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </div>
                        </div>
                        <div class="override-column">
                          <div class="override-column-header">
                            <h4>Exclude Teams</h4>
                            <span class="helper-text">{{ rule.excludeTeamIds.length }}</span>
                          </div>
                          <div class="chip-list rule-chip-list">
                            @if (rule.excludeTeamIds.length === 0) {
                              <div class="empty-chip-state">Add a team here to exclude it.</div>
                            }
                            @for (teamId of rule.excludeTeamIds; track trackByTeamId($index, teamId)) {
                              <div class="chip chip-warn">
                                <span class="chip-label">{{ describeTeamById(teamId) }}</span>
                                <button mat-icon-button type="button" [attr.aria-label]="'Remove excluded team ' + describeTeamById(teamId)" (click)="removeExcludedTeam(rule, teamId)">
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="rule-target-section">
                      <div class="subsection-header user-override-header">
                        <div>
                          <p class="helper-text subsection-description">
                            Search companies by name or ID, then choose Include or Exclude for this rule.
                          </p>
                        </div>
                        <div class="user-override-actions">
                          <button mat-raised-button color="primary" type="button"
                            [disabled]="!rule.selectedCompanyId"
                            (click)="addPendingRuleCompany(rule, 'include')">
                            Include
                          </button>
                          <button mat-raised-button color="warn" type="button"
                            [disabled]="!rule.selectedCompanyId"
                            (click)="addPendingRuleCompany(rule, 'exclude')">
                            Exclude
                          </button>
                        </div>
                      </div>
                      <div class="user-search-grid target-search-grid">
                        <mat-form-field appearance="outline">
                          <mat-label>Search companies</mat-label>
                          <input matInput [(ngModel)]="rule.companySearchQuery"
                            (ngModelChange)="searchRuleCompanies(rule)"
                            placeholder="Company name or ID" />
                        </mat-form-field>
                        <mat-form-field appearance="outline">
                          <mat-label>Manual company ID</mat-label>
                          <input matInput [(ngModel)]="rule.manualCompanyId" placeholder="companyId" />
                        </mat-form-field>
                        <button mat-stroked-button class="inline-action" type="button" (click)="selectManualRuleCompany(rule)">
                          Select Company ID
                        </button>
                      </div>
                      @if (rule.companySearchResults.length > 0) {
                        <div class="user-search-results">
                          @for (company of rule.companySearchResults; track trackByCompanyResultId($index, company)) {
                            <button type="button" class="user-result"
                              [class.user-result-selected]="company.id === rule.selectedCompanyId"
                              (click)="selectSearchedCompany(rule, company)">
                              <span>{{ company.name }}</span>
                              <strong>{{ company.id }}</strong>
                            </button>
                          }
                        </div>
                      }
                      <div class="selected-user-card" [class.selected-user-card-empty]="!rule.selectedCompanyId">
                        @if (rule.selectedCompanyId) {
                          <div>
                            <span class="eyebrow">Selected Company</span>
                            <div class="selected-user-meta">
                              <strong>{{ rule.selectedCompanyId }}</strong>
                              <span class="helper-text">{{ rule.selectedCompanyLabel }}</span>
                            </div>
                          </div>
                        } @else {
                          <span class="helper-text">Pick a search result or use a manual company ID to enable the Include and Exclude actions.</span>
                        }
                        @if (rule.selectedCompanyId) {
                          <button mat-button type="button"
                            (click)="clearPendingRuleCompanySelection(rule)">
                            Clear Selection
                          </button>
                        }
                      </div>
                      <div class="override-grid target-override-grid">
                        <div class="override-column">
                          <div class="override-column-header">
                            <h4>Include Companies</h4>
                            <span class="helper-text">{{ rule.companyIds.length }}</span>
                          </div>
                          <div class="chip-list rule-chip-list">
                            @if (rule.companyIds.length === 0) {
                              <div class="empty-chip-state">Add a company here to include it.</div>
                            }
                            @for (companyId of rule.companyIds; track trackByCompanyId($index, companyId)) {
                              <div class="chip">
                                <span class="chip-label">{{ describeCompanyById(companyId) }}</span>
                                <button mat-icon-button type="button" (click)="removeIncludedCompany(rule, companyId)">
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </div>
                        </div>
                        <div class="override-column">
                          <div class="override-column-header">
                            <h4>Exclude Companies</h4>
                            <span class="helper-text">{{ rule.excludeCompanyIds.length }}</span>
                          </div>
                          <div class="chip-list rule-chip-list">
                            @if (rule.excludeCompanyIds.length === 0) {
                              <div class="empty-chip-state">Add a company here to exclude it.</div>
                            }
                            @for (companyId of rule.excludeCompanyIds; track trackByCompanyId($index, companyId)) {
                              <div class="chip chip-warn">
                                <span class="chip-label">{{ describeCompanyById(companyId) }}</span>
                                <button mat-icon-button type="button" (click)="removeExcludedCompany(rule, companyId)">
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                    <div class="rule-target-section">
                      <div class="subsection-header user-override-header">
                        <div>
                          <p class="helper-text subsection-description">
                            Search partners by name or ID, then choose Include or Exclude for this rule.
                          </p>
                        </div>
                        <div class="user-override-actions">
                          <button
                            mat-raised-button
                            color="primary"
                            type="button"
                            [disabled]="!rule.selectedPartnerId"
                            (click)="addPendingRulePartner(rule, 'include')">
                            Include
                          </button>
                          <button
                            mat-raised-button
                            color="warn"
                            type="button"
                            [disabled]="!rule.selectedPartnerId"
                            (click)="addPendingRulePartner(rule, 'exclude')">
                            Exclude
                          </button>
                        </div>
                      </div>
                      <div class="user-search-grid partner-search-grid">
                        <mat-form-field appearance="outline">
                          <mat-label>Search partners</mat-label>
                          <input
                            matInput
                            [(ngModel)]="rule.partnerSearchQuery"
                            (ngModelChange)="searchRulePartners(rule)"
                            placeholder="Partner name or ID" />
                        </mat-form-field>
                        <mat-form-field appearance="outline">
                          <mat-label>Manual partner ID</mat-label>
                          <input matInput [(ngModel)]="rule.manualPartnerId" placeholder="partnerId" />
                        </mat-form-field>
                        <button mat-stroked-button class="inline-action" type="button" (click)="selectManualRulePartner(rule)">
                          Select Partner ID
                        </button>
                      </div>
                      @if (rule.partnerSearchResults.length > 0) {
                        <div class="user-search-results">
                          @for (partner of rule.partnerSearchResults; track trackByPartnerResultId($index, partner)) {
                            <button
                              type="button"
                              class="user-result"
                              [class.user-result-selected]="partner.id === rule.selectedPartnerId"
                              (click)="selectSearchedPartner(rule, partner)">
                              <span>{{ partner.name }}</span>
                              <strong>{{ partner.id }}</strong>
                            </button>
                          }
                        </div>
                      }
                      <div class="selected-user-card" [class.selected-user-card-empty]="!rule.selectedPartnerId">
                        @if (rule.selectedPartnerId) {
                          <div>
                            <span class="eyebrow">Selected Partner</span>
                            <div class="selected-user-meta">
                              <strong>{{ rule.selectedPartnerId }}</strong>
                              <span class="helper-text">{{ rule.selectedPartnerLabel }}</span>
                            </div>
                          </div>
                        } @else {
                          <span class="helper-text">Pick a search result or use a manual partner ID to enable the Include and Exclude actions.</span>
                        }
                        @if (rule.selectedPartnerId) {
                          <button
                            mat-button
                            type="button"
                            (click)="clearPendingRulePartnerSelection(rule)">
                            Clear Selection
                          </button>
                        }
                      </div>
                      <div class="override-grid partner-override-grid">
                        <div class="override-column">
                          <div class="override-column-header">
                            <h4>Include Partner IDs</h4>
                            <span class="helper-text">{{ rule.partnerIds.length }}</span>
                          </div>
                          <div class="chip-list rule-chip-list">
                            @if (rule.partnerIds.length === 0) {
                              <div class="empty-chip-state">
                                Add a partner here to include it.
                              </div>
                            }
                            @for (partnerId of rule.partnerIds; track trackByPartnerId($index, partnerId)) {
                              <div class="chip">
                                <span class="chip-label">{{ describePartnerById(partnerId) }}</span>
                                <button mat-icon-button type="button" (click)="removeIncludedPartner(rule, partnerId)">
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </div>
                        </div>
                        <div class="override-column">
                          <div class="override-column-header">
                            <h4>Exclude Partner IDs</h4>
                            <span class="helper-text">{{ rule.excludePartnerIds.length }}</span>
                          </div>
                          <div class="chip-list rule-chip-list">
                            @if (rule.excludePartnerIds.length === 0) {
                              <div class="empty-chip-state">
                                Add a partner here to exclude it.
                              </div>
                            }
                            @for (partnerId of rule.excludePartnerIds; track trackByPartnerId($index, partnerId)) {
                              <div class="chip chip-warn">
                                <span class="chip-label">{{ describePartnerById(partnerId) }}</span>
                                <button mat-icon-button type="button" (click)="removeExcludedPartner(rule, partnerId)">
                                  <mat-icon>close</mat-icon>
                                </button>
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                    </div>
                    <mat-form-field appearance="outline">
                      <mat-label>Include states</mat-label>
                      <textarea matInput rows="3" [(ngModel)]="rule.statesText" placeholder="CO, TX"></textarea>
                    </mat-form-field>
                    <mat-form-field appearance="outline">
                      <mat-label>Exclude states</mat-label>
                      <textarea matInput rows="3" [(ngModel)]="rule.excludeStatesText"></textarea>
                    </mat-form-field>
                  </div>
                  @if (getRuleValidationMessage(rule, ruleIndex); as ruleValidationMessage) {
                    <p class="validation-message rule-validation-message">
                      {{ ruleValidationMessage }}
                    </p>
                  }
                </mat-card>
              }
            </div>
          }
        </mat-card>
        <mat-card class="subsection-card" [class.subsection-card-collapsed]="!sectionExpanded.evaluationPreview">
          <button
            type="button"
            class="subsection-toggle"
            [attr.aria-expanded]="sectionExpanded.evaluationPreview"
            (click)="toggleSection('evaluationPreview')">
            <div class="subsection-toggle-copy">
              <h3>Test Evaluation</h3>
              <span class="section-summary">Preview draft or saved behavior</span>
            </div>
            <mat-icon class="subsection-toggle-icon" [class.subsection-toggle-icon-expanded]="sectionExpanded.evaluationPreview">
              expand_more
            </mat-icon>
          </button>
          @if (sectionExpanded.evaluationPreview) {
            <div class="subsection-content">
              <div class="subsection-header">
                <div>
                  <p class="helper-text subsection-description">
                    Preview the current editor draft, or compare it with the saved flag record in the connected backend. Use the user's own team, company, partner, and state values below.
                    Draft preview uses only the entered values. Saved evaluation also resolves missing user attributes and team memberships from the backend.
                  </p>
                </div>
                <div class="preview-actions">
                  <button mat-raised-button color="primary" type="button" [disabled]="previewLoading" (click)="previewDraft()">
                    Preview Draft
                  </button>
                  <button mat-stroked-button type="button" [disabled]="previewLoading" (click)="previewSaved()">
                    {{ previewLoading ? 'Running...' : 'Preview Saved' }}
                  </button>
                </div>
              </div>
              <div class="rule-grid">
                <mat-form-field appearance="outline">
                  <mat-label>App</mat-label>
                  <mat-select [(ngModel)]="previewContext.app">
                    <mat-option value="team-admin">Team Admin</mat-option>
                    <mat-option value="customer-app">Customer App</mat-option>
                    <mat-option value="backend">Backend</mat-option>
                  </mat-select>
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>User ID</mat-label>
                  <input matInput [(ngModel)]="previewContext.userId" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Team ID</mat-label>
                  <input matInput [(ngModel)]="previewContext.teamId" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Company ID</mat-label>
                  <input matInput [(ngModel)]="previewContext.companyId" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>Partner ID</mat-label>
                  <input matInput [(ngModel)]="previewContext.partnerId" />
                </mat-form-field>
                <mat-form-field appearance="outline">
                  <mat-label>State</mat-label>
                  <input matInput [(ngModel)]="previewContext.state" />
                </mat-form-field>
              </div>
              @if (previewResult) {
                <div class="preview-result">
                  <div>
                    <strong>Previewing:</strong> {{ previewMode === 'draft' ? 'Current editor draft' : 'Saved backend record' }}
                  </div>
                  <div class="preview-pill" [class.preview-pill-disabled]="!previewResult.enabled">
                    {{ previewResult.enabled ? 'Enabled' : 'Disabled' }}
                  </div>
                  <div>
                    <strong>Source:</strong> {{ previewResult.source }}
                  </div>
                  @if (previewResult.matchedRuleId) {
                    <div>
                      <strong>Matched Rule:</strong> {{ previewResult.matchedRuleId }}
                    </div>
                  }
                </div>
              }
            </div>
          }
        </mat-card>
      </section>
      <ng-container *ngTemplateOutlet="transferSection"></ng-container>
    </div>
  }
</div>
